import {
    Avatar,
    Box,
    Card,
    CardContent,
    Grid,
    Paper,
    Typography,
    useTheme,
} from '@mui/material';
import React from 'react';
import {
    useGetEarningsQuery,
    useGetAddOnStatsQuery,
    useGetExpenseStatsQuery,
    useGetJobStatsQuery,
    useGetRsvpSentStatsQuery,
    useGetRsvpResponseStatsQuery,
    useGetGuestUserStatsQuery,
    useGetExternalGuestAttendeeStatsQuery,
    useGetTotalBookingsStatsQuery,
    useGetNewBookingStatsQuery,
    useGetOccupancyQuery,
} from '../../../../app/Slices/SevenDayApiSlice';
import { useNavigate } from 'react-router-dom';
import {
    AttachMoney,
    CalendarMonth,
    Email,
    Engineering,
    Extension,
    HowToReg,
    NavigateNext,
    People,
} from '@mui/icons-material';
import IncomeCard from '../v2/IncomeCard';
import UserRole from '../../../../shared/AuthRoles';
import { Period } from '../../../enums/reports';
import { AdminRole, OwnerRole } from '../../../enums/users';
import SkeletonLayout from '../v2/SkeletonLayout';
import { formatCurrencyShort } from '../../../../shared/utils';
import {
    formatExperienceBreakdown,
    formatVisitBreakdown,
} from '../newBookingStatsFormat';
import {
    formatProfileBreakdown,
    formatStatusBreakdown,
} from '../guestUserStatsFormat';
import { periodToStatsBreakdown, periodToStatsRange } from '../dashboardPeriod';

type AdminDashboardSmallerStatsProps = {
    period?: Period;
};

export default function AdminDashboardSmallerStats({
    period = Period.CurrentWeek,
}: AdminDashboardSmallerStatsProps) {
    const loggedInUser = UserRole();
    const theme = useTheme();

    const { data: projectedEarnings } = useGetEarningsQuery({
        year: new Date().getFullYear(),
        month: new Date().getMonth(),
        owner: loggedInUser?.company,
        projected: true,
    });

    const { data: occupancy } = useGetOccupancyQuery({
        period: Period.CurrentMonth,
    });

    const { data: newBookingStats } = useGetNewBookingStatsQuery(
        {
            range: periodToStatsRange(period),
            breakdown: periodToStatsBreakdown(period),
        },
        { skip: loggedInUser?.role !== AdminRole.Admin },
    );
    const { data: expenseStats } = useGetExpenseStatsQuery(
        {
            range: periodToStatsRange(period),
            breakdown: periodToStatsBreakdown(period),
        },
        { skip: loggedInUser?.role !== AdminRole.Admin },
    );
    const { data: addOnStats } = useGetAddOnStatsQuery(
        {
            range: periodToStatsRange(period),
            breakdown: periodToStatsBreakdown(period),
        },
        { skip: loggedInUser?.role !== AdminRole.Admin },
    );
    const { data: jobStats } = useGetJobStatsQuery(
        {
            range: periodToStatsRange(period),
            breakdown: periodToStatsBreakdown(period),
        },
        { skip: loggedInUser?.role !== AdminRole.Admin },
    );
    const { data: rsvpSentStats } = useGetRsvpSentStatsQuery(
        {
            range: periodToStatsRange(period),
            breakdown: periodToStatsBreakdown(period),
        },
        { skip: loggedInUser?.role !== AdminRole.Admin },
    );
    const { data: rsvpResponseStats } = useGetRsvpResponseStatsQuery(
        {
            range: periodToStatsRange(period),
            breakdown: periodToStatsBreakdown(period),
        },
        { skip: loggedInUser?.role !== AdminRole.Admin },
    );
    const { data: guestUserStats } = useGetGuestUserStatsQuery(undefined, {
        skip: loggedInUser?.role !== AdminRole.Admin,
    });
    const { data: externalGuestAttendeeStats } =
        useGetExternalGuestAttendeeStatsQuery(
            {
                range: periodToStatsRange(period),
                breakdown: periodToStatsBreakdown(period),
            },
            { skip: loggedInUser?.role !== AdminRole.Admin },
        );
    const { data: totalBookingsStats } = useGetTotalBookingsStatsQuery(
        undefined,
        { skip: loggedInUser?.role !== AdminRole.Admin },
    );

    const navigate = useNavigate();

    return (
        <>
            {loggedInUser?.role === OwnerRole.Admin && (
                <Grid item xs>
                    <Card
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/earnings?projected=true`)}
                    >
                        <CardContent sx={{ m: 1 }}>
                            {projectedEarnings ? (
                                <div>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            gap: 1,
                                        }}
                                    >
                                        <Avatar
                                            variant='rounded'
                                            sx={{
                                                height: 25,
                                                width: 25,
                                                bgcolor:
                                                    theme.palette.neutral.main,
                                                color: 'text.primary',
                                            }}
                                        >
                                            <AttachMoney fontSize='small' />
                                        </Avatar>
                                        <Typography sx={{ fontWeight: 'bold' }}>
                                            Projected Earnings
                                        </Typography>
                                        <NavigateNext />
                                    </Box>
                                    <Typography
                                        sx={{
                                            fontSize: '3rem',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        {`$${(
                                            projectedEarnings?.amount / 1000
                                        ).toFixed(0)}k`}
                                    </Typography>
                                    <Typography
                                        sx={{ lineHeight: 1 }}
                                        variant='overline'
                                    >
                                        This month
                                    </Typography>
                                </div>
                            ) : (
                                <SkeletonLayout extended />
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            )}
            {loggedInUser?.role === OwnerRole.Admin && (
                <Grid item xs={12} lg={4}>
                    <IncomeCard />
                </Grid>
            )}
            {loggedInUser?.role === OwnerRole.Admin && (
                <Grid item xs>
                    <Card
                        sx={{
                            cursor: 'pointer',
                            transition: '0.3s ease-in-out',
                        }}
                        onClick={() => navigate('/reports/occupancy')}
                    >
                        <CardContent sx={{ m: 1 }}>
                            {occupancy ? (
                                <div>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            gap: 1,
                                        }}
                                    >
                                        <Avatar
                                            variant='rounded'
                                            sx={{
                                                height: 25,
                                                width: 25,
                                                bgcolor:
                                                    theme.palette.neutral.main,
                                                color: 'text.primary',
                                            }}
                                        >
                                            <People fontSize='small' />
                                        </Avatar>
                                        <Typography sx={{ fontWeight: 'bold' }}>
                                            Average Occupancy
                                        </Typography>
                                        <NavigateNext />
                                    </Box>
                                    <Typography
                                        sx={{
                                            fontSize: '3rem',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        {occupancy && occupancy?.length > 0
                                            ? (
                                                  occupancy?.reduce(
                                                      (
                                                          sum: number,
                                                          item: any,
                                                      ) =>
                                                          sum +
                                                          item.availableNightsOccupancy,
                                                      0,
                                                  ) / occupancy?.length
                                              ).toFixed(1)
                                            : 'N/A'}
                                        %
                                    </Typography>
                                    <Typography
                                        sx={{ lineHeight: 1 }}
                                        variant='overline'
                                    >
                                        This month
                                    </Typography>
                                </div>
                            ) : (
                                <SkeletonLayout extended />
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            )}

            {loggedInUser?.role === AdminRole.Admin && (
                <Grid item xs={12}>
                    <Paper elevation={0}>
                        <Typography variant='subHeading' sx={{ lineHeight: 2 }}>
                            Reservations
                        </Typography>
                        <Grid container spacing={2} sx={{ pt: 1 }}>
                            <Grid item xs>
                                <Card
                                    sx={{
                                        transition: '0.3s ease-in-out',
                                        height: '100%',
                                    }}
                                >
                                    <CardContent sx={{ m: 1 }}>
                                        {totalBookingsStats ? (
                                            <div>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        gap: 0.5,
                                                    }}
                                                >
                                                    <Avatar
                                                        variant='rounded'
                                                        sx={{
                                                            height: 25,
                                                            width: 25,
                                                            bgcolor:
                                                                theme.palette
                                                                    .neutral
                                                                    .main,
                                                            color: 'text.primary',
                                                        }}
                                                    >
                                                        <CalendarMonth fontSize='small' />
                                                    </Avatar>
                                                    <Box>
                                                        <Typography
                                                            sx={{
                                                                fontWeight:
                                                                    'bold',
                                                            }}
                                                        >
                                                            Total Bookings (All
                                                            Time)
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                <Typography
                                                    sx={{
                                                        fontSize: '3rem',
                                                        fontWeight: 'bold',
                                                    }}
                                                >
                                                    {totalBookingsStats.totalBookings.toLocaleString()}
                                                </Typography>
                                            </div>
                                        ) : (
                                            <SkeletonLayout extended />
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs>
                                <Card
                                    sx={{
                                        cursor: 'pointer',
                                        transition: '0.3s ease-in-out',
                                        height: '100%',
                                    }}
                                    onClick={() =>
                                        navigate(
                                            `/dashboard/new-bookings?period=${period}`,
                                        )
                                    }
                                >
                                    <CardContent sx={{ m: 1 }}>
                                        {newBookingStats ? (
                                            <div>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        gap: 0.5,
                                                    }}
                                                >
                                                    <Avatar
                                                        variant='rounded'
                                                        sx={{
                                                            height: 25,
                                                            width: 25,
                                                            bgcolor:
                                                                theme.palette
                                                                    .neutral
                                                                    .main,
                                                            color: 'text.primary',
                                                        }}
                                                    >
                                                        <CalendarMonth fontSize='small' />
                                                    </Avatar>
                                                    <Typography
                                                        sx={{
                                                            fontWeight: 'bold',
                                                        }}
                                                    >
                                                        New Reservations
                                                    </Typography>
                                                    <NavigateNext />
                                                </Box>
                                                <Typography
                                                    sx={{
                                                        fontSize: '3rem',
                                                        fontWeight: 'bold',
                                                    }}
                                                >
                                                    {
                                                        newBookingStats.totalNewBookings
                                                    }
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        lineHeight: 1.3,
                                                        display: 'block',
                                                        whiteSpace: 'pre-line',
                                                    }}
                                                    variant='overline'
                                                >
                                                    {[
                                                        formatExperienceBreakdown(
                                                            newBookingStats.experienceTypeBreakdown,
                                                        ) || 'No reservations',
                                                        formatVisitBreakdown(
                                                            newBookingStats.visitTypeBreakdown,
                                                        ) || 'No visit types',
                                                    ].join('\n')}
                                                </Typography>
                                            </div>
                                        ) : (
                                            <SkeletonLayout extended />
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs>
                                <Card
                                    sx={{
                                        cursor: 'pointer',
                                        transition: '0.3s ease-in-out',
                                        height: '100%',
                                    }}
                                    onClick={() =>
                                        navigate(
                                            `/dashboard/add-ons-booked?period=${period}`,
                                        )
                                    }
                                >
                                    <CardContent sx={{ m: 1 }}>
                                        {addOnStats ? (
                                            <div>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        gap: 0.5,
                                                    }}
                                                >
                                                    <Avatar
                                                        variant='rounded'
                                                        sx={{
                                                            height: 25,
                                                            width: 25,
                                                            bgcolor:
                                                                theme.palette
                                                                    .neutral
                                                                    .main,
                                                            color: 'text.primary',
                                                        }}
                                                    >
                                                        <Extension fontSize='small' />
                                                    </Avatar>
                                                    <Typography
                                                        sx={{
                                                            fontWeight: 'bold',
                                                        }}
                                                    >
                                                        Total Add-ons Booked
                                                    </Typography>
                                                    <NavigateNext />
                                                </Box>
                                                <Typography
                                                    sx={{
                                                        fontSize: '3rem',
                                                        fontWeight: 'bold',
                                                    }}
                                                >
                                                    {
                                                        addOnStats.totalAddOnsBooked
                                                    }
                                                </Typography>
                                            </div>
                                        ) : (
                                            <SkeletonLayout extended />
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs>
                                <Card
                                    sx={{
                                        cursor: 'pointer',
                                        transition: '0.3s ease-in-out',
                                        height: '100%',
                                    }}
                                    onClick={() =>
                                        navigate(
                                            `/dashboard/expense-claimed?period=${period}`,
                                        )
                                    }
                                >
                                    <CardContent sx={{ m: 1 }}>
                                        {expenseStats ? (
                                            <div>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        gap: 0.5,
                                                    }}
                                                >
                                                    <Avatar
                                                        variant='rounded'
                                                        sx={{
                                                            height: 25,
                                                            width: 25,
                                                            bgcolor:
                                                                theme.palette
                                                                    .neutral
                                                                    .main,
                                                            color: 'text.primary',
                                                        }}
                                                    >
                                                        <AttachMoney fontSize='small' />
                                                    </Avatar>
                                                    <Typography
                                                        sx={{
                                                            fontWeight: 'bold',
                                                        }}
                                                    >
                                                        Total Expense Claimed
                                                    </Typography>
                                                    <NavigateNext />
                                                </Box>
                                                <Typography
                                                    sx={{
                                                        fontSize: '3rem',
                                                        fontWeight: 'bold',
                                                    }}
                                                >
                                                    {expenseStats.expenseCount}
                                                </Typography>
                                                <Typography
                                                    sx={{ lineHeight: 1 }}
                                                    variant='overline'
                                                >
                                                    {formatCurrencyShort(
                                                        expenseStats.totalExpenseClaimed,
                                                    )}
                                                </Typography>
                                            </div>
                                        ) : (
                                            <SkeletonLayout extended />
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs>
                                <Card
                                    sx={{
                                        cursor: 'pointer',
                                        transition: '0.3s ease-in-out',
                                        height: '100%',
                                    }}
                                    onClick={() =>
                                        navigate(
                                            `/dashboard/jobs-created?period=${period}`,
                                        )
                                    }
                                >
                                    <CardContent sx={{ m: 1 }}>
                                        {jobStats ? (
                                            <div>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        gap: 0.5,
                                                    }}
                                                >
                                                    <Avatar
                                                        variant='rounded'
                                                        sx={{
                                                            height: 25,
                                                            width: 25,
                                                            bgcolor:
                                                                theme.palette
                                                                    .neutral
                                                                    .main,
                                                            color: 'text.primary',
                                                        }}
                                                    >
                                                        <Engineering fontSize='small' />
                                                    </Avatar>
                                                    <Typography
                                                        sx={{
                                                            fontWeight: 'bold',
                                                        }}
                                                    >
                                                        Total Jobs Created
                                                    </Typography>
                                                    <NavigateNext />
                                                </Box>
                                                <Typography
                                                    sx={{
                                                        fontSize: '3rem',
                                                        fontWeight: 'bold',
                                                    }}
                                                >
                                                    {jobStats.totalJobsCreated}
                                                </Typography>
                                            </div>
                                        ) : (
                                            <SkeletonLayout extended />
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>
            )}
            {loggedInUser?.role === AdminRole.Admin && (
                <Grid item xs={12}>
                    <Paper elevation={0}>
                        <Typography variant='subHeading' sx={{ lineHeight: 2 }}>
                            Guests
                        </Typography>
                        <Grid container spacing={2} sx={{ pt: 1 }}>
                            <Grid item xs>
                                <Card
                                    sx={{
                                        cursor: 'pointer',
                                        transition: '0.3s ease-in-out',
                                        height: '100%',
                                    }}
                                    onClick={() =>
                                        navigate('/dashboard/guest-users')
                                    }
                                >
                                    <CardContent sx={{ m: 1 }}>
                                        {guestUserStats ? (
                                            <div>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        gap: 0.5,
                                                    }}
                                                >
                                                    <Avatar
                                                        variant='rounded'
                                                        sx={{
                                                            height: 25,
                                                            width: 25,
                                                            bgcolor:
                                                                theme.palette
                                                                    .neutral
                                                                    .main,
                                                            color: 'text.primary',
                                                        }}
                                                    >
                                                        <People fontSize='small' />
                                                    </Avatar>
                                                    <Box
                                                        sx={{
                                                            flex: 1,
                                                            minWidth: 0,
                                                        }}
                                                    >
                                                        <Typography
                                                            sx={{
                                                                fontWeight:
                                                                    'bold',
                                                            }}
                                                        >
                                                            Total Guest Users
                                                            (All Time)
                                                        </Typography>
                                                    </Box>
                                                    <NavigateNext />
                                                </Box>
                                                <Typography
                                                    sx={{
                                                        fontSize: '3rem',
                                                        fontWeight: 'bold',
                                                    }}
                                                >
                                                    {
                                                        guestUserStats.totalGuestUsers
                                                    }
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        lineHeight: 1.3,
                                                        display: 'block',
                                                        whiteSpace: 'pre-line',
                                                    }}
                                                    variant='overline'
                                                >
                                                    {[
                                                        formatStatusBreakdown(
                                                            guestUserStats,
                                                        ),
                                                        formatProfileBreakdown(
                                                            guestUserStats,
                                                        ),
                                                    ].join('\n')}
                                                </Typography>
                                            </div>
                                        ) : (
                                            <SkeletonLayout extended />
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs>
                                <Card
                                    sx={{
                                        cursor: 'pointer',
                                        transition: '0.3s ease-in-out',
                                        height: '100%',
                                    }}
                                    onClick={() =>
                                        navigate(
                                            `/dashboard/external-guest-attendees?period=${period}`,
                                        )
                                    }
                                >
                                    <CardContent sx={{ m: 1 }}>
                                        {externalGuestAttendeeStats ? (
                                            <div>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        gap: 0.5,
                                                    }}
                                                >
                                                    <Avatar
                                                        variant='rounded'
                                                        sx={{
                                                            height: 25,
                                                            width: 25,
                                                            bgcolor:
                                                                theme.palette
                                                                    .neutral
                                                                    .main,
                                                            color: 'text.primary',
                                                        }}
                                                    >
                                                        <People fontSize='small' />
                                                    </Avatar>
                                                    <Typography
                                                        sx={{
                                                            fontWeight: 'bold',
                                                        }}
                                                    >
                                                        External Guest Attendees
                                                    </Typography>
                                                    <NavigateNext />
                                                </Box>
                                                <Typography
                                                    sx={{
                                                        fontSize: '3rem',
                                                        fontWeight: 'bold',
                                                    }}
                                                >
                                                    {
                                                        externalGuestAttendeeStats.totalExternalGuestAttendees
                                                    }
                                                </Typography>
                                            </div>
                                        ) : (
                                            <SkeletonLayout extended />
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs>
                                <Card
                                    sx={{
                                        cursor: 'pointer',
                                        transition: '0.3s ease-in-out',
                                        height: '100%',
                                    }}
                                    onClick={() =>
                                        navigate(
                                            `/dashboard/rsvps-sent?period=${period}`,
                                        )
                                    }
                                >
                                    <CardContent sx={{ m: 1 }}>
                                        {rsvpSentStats ? (
                                            <div>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        gap: 0.5,
                                                    }}
                                                >
                                                    <Avatar
                                                        variant='rounded'
                                                        sx={{
                                                            height: 25,
                                                            width: 25,
                                                            bgcolor:
                                                                theme.palette
                                                                    .neutral
                                                                    .main,
                                                            color: 'text.primary',
                                                        }}
                                                    >
                                                        <Email fontSize='small' />
                                                    </Avatar>
                                                    <Typography
                                                        sx={{
                                                            fontWeight: 'bold',
                                                        }}
                                                    >
                                                        RSVPs Sent
                                                    </Typography>
                                                    <NavigateNext />
                                                </Box>
                                                <Typography
                                                    sx={{
                                                        fontSize: '3rem',
                                                        fontWeight: 'bold',
                                                    }}
                                                >
                                                    {rsvpSentStats.totalSent}
                                                </Typography>
                                                <Typography
                                                    sx={{ lineHeight: 1 }}
                                                    variant='overline'
                                                >
                                                    {`${rsvpSentStats.totalNotSent} not sent`}
                                                </Typography>
                                            </div>
                                        ) : (
                                            <SkeletonLayout extended />
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs>
                                <Card
                                    sx={{
                                        cursor: 'pointer',
                                        transition: '0.3s ease-in-out',
                                        height: '100%',
                                    }}
                                    onClick={() =>
                                        navigate(
                                            `/dashboard/rsvps-responded?period=${period}`,
                                        )
                                    }
                                >
                                    <CardContent sx={{ m: 1 }}>
                                        {rsvpResponseStats ? (
                                            <div>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        gap: 0.5,
                                                    }}
                                                >
                                                    <Avatar
                                                        variant='rounded'
                                                        sx={{
                                                            height: 25,
                                                            width: 25,
                                                            bgcolor:
                                                                theme.palette
                                                                    .neutral
                                                                    .main,
                                                            color: 'text.primary',
                                                        }}
                                                    >
                                                        <HowToReg fontSize='small' />
                                                    </Avatar>
                                                    <Typography
                                                        sx={{
                                                            fontWeight: 'bold',
                                                        }}
                                                    >
                                                        RSVP Responses
                                                    </Typography>
                                                    <NavigateNext />
                                                </Box>
                                                <Typography
                                                    sx={{
                                                        fontSize: '3rem',
                                                        fontWeight: 'bold',
                                                    }}
                                                >
                                                    {rsvpResponseStats.totalAccepted +
                                                        rsvpResponseStats.totalDeclined}
                                                </Typography>
                                                <Typography
                                                    sx={{ lineHeight: 1 }}
                                                    variant='overline'
                                                >
                                                    {`${rsvpResponseStats.totalAccepted} accepted · ${rsvpResponseStats.totalDeclined} declined · ${rsvpResponseStats.totalPending} pending`}
                                                </Typography>
                                            </div>
                                        ) : (
                                            <SkeletonLayout extended />
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>
            )}
        </>
    );
}
